import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Table,
  Tag,
  Tooltip,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Typography,
  Alert,
  Popconfirm,
  message,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  PoweroffOutlined,
  ThunderboltOutlined,
  CloudServerOutlined,
} from '@ant-design/icons';
import {
  listBackends,
  createBackend,
  deleteBackend,
  bootBackend,
  shutdownBackend,
  updateBackendSSH,
  listOnethingInstances,
  getAutoscaleConfig,
  putAutoscaleConfig,
  type Backend,
  type UpdateBackendSSHInput,
  type BackendStatus,
  type BackendSummary,
  type CreateBackendInput,
  type OnethingInstance,
  type OnethingInstanceStatus,
  type EngineReadinessMap,
  type AutoscaleConfig,
} from '@/api/backends';
import type { ApiError } from '@/api/client';
import { formatDateTime } from '@/utils/format';

const { Title, Paragraph } = Typography;

const STATUS_META: Record<BackendStatus, { color: string; label: string }> = {
  online: { color: 'green', label: '运行中' },
  idle: { color: 'blue', label: '空闲' },
  offline: { color: 'red', label: '离线' },
  maintenance: { color: 'gold', label: '维护中' },
};

// OneThingAI 实例状态映射 (300=运行, 800=停止, 其余为过渡态).
const INSTANCE_STATUS_META: Record<
  number,
  { color: string; label: string }
> = {
  100: { color: 'processing', label: '部署中' },
  200: { color: 'processing', label: '启动中' },
  300: { color: 'green', label: '运行中' },
  400: { color: 'orange', label: '关机中' },
  800: { color: 'default', label: '已停止' },
};

function instanceStatusMeta(s: OnethingInstanceStatus) {
  return INSTANCE_STATUS_META[s] ?? { color: 'default', label: `状态 ${s}` };
}

// gpu-availability-view: GPU 可用性徽章. true=绿"GPU可用", false=灰"不可用", null/undefined=未知.
function GpuAvailabilityTag({ available }: { available?: boolean | null }) {
  if (available === true) return <Tag color="green">GPU可用</Tag>;
  if (available === false) return <Tag color="default">不可用</Tag>;
  return <Tag color="default">未知</Tag>;
}

/** boot/shutdown 在 token 未配时返回 message 含"OneThingAI API 未配置" (list 走
 *  configured 字段, 不经此). 据此置灰实例操作。 */
function isNotConfigured(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    typeof (e as ApiError).message === 'string' &&
    (e as ApiError).message.includes('OneThingAI API 未配置')
  );
}

const EMPTY_SUMMARY: BackendSummary = {
  total: 0,
  online: 0,
  offline: 0,
  idle: 0,
  maintenance: 0,
  pending_total: 0,
};

// Engines shown in the readiness matrix, in display order.
const CALIBRATION_ENGINES = ['reactor', 'reactor_multi', 'pulid_reactor', 'jimeng'] as const;

// EngineReadinessMatrix renders a compact readiness badge row for one backend.
// Shows ✅ / ❌(缺项) for each engine, plus last smoke-test time on hover.
function EngineReadinessMatrix({ readiness }: { readiness?: EngineReadinessMap }) {
  if (!readiness || Object.keys(readiness).length === 0) {
    return <span style={{ color: '#aaa', fontSize: 12 }}>未探测</span>;
  }
  return (
    <Space size={4} wrap>
      {CALIBRATION_ENGINES.map((engine) => {
        const entry = readiness[engine];
        if (!entry) {
          // Not in map = not yet probed for this engine.
          return (
            <Tooltip key={engine} title="未探测">
              <Tag color="default" style={{ fontSize: 11 }}>
                {engine} ?
              </Tag>
            </Tooltip>
          );
        }
        const smokeLabel = entry.smoke_at
          ? `冲烟: ${new Date(entry.smoke_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`
          : '未冲烟';
        const tip = entry.ready
          ? smokeLabel
          : `未就绪: ${entry.reason ?? (entry.missing?.join(', ') ?? '未知原因')}`;
        return (
          <Tooltip key={engine} title={tip}>
            <Tag
              color={entry.ready ? 'success' : 'error'}
              style={{ fontSize: 11 }}
            >
              {engine} {entry.ready ? '✓' : '✗'}
            </Tag>
          </Tooltip>
        );
      })}
    </Space>
  );
}

/**
 * GPU 算力池页 (gpu-backend-pool).
 *
 * 平台级跨 App 共享的 Onething backend 池. 健康检查探活 + 负载感知调度 +
 * 故障转移: 一台关机自动绕开 (解决 502 单点), 全部离线降级即梦/flux 兜底链.
 */
export function BackendsListPage() {
  const [backends, setBackends] = useState<Backend[]>([]);
  const [summary, setSummary] = useState<BackendSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateBackendInput>();
  // 行内 boot/shutdown 进行中的 backend ext_id (按钮 loading + 防重复点).
  const [busyId, setBusyId] = useState<string | null>(null);
  // OneThingAI token 未配 → 置灰所有实例级操作.
  const [onethingDisabled, setOnethingDisabled] = useState(false);

  // ---- "从 OneThingAI 添加实例" Modal 状态 ----
  const [addOpen, setAddOpen] = useState(false);
  const [instances, setInstances] = useState<OnethingInstance[]>([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [picked, setPicked] = useState<OnethingInstance | null>(null);
  const [registering, setRegistering] = useState(false);
  const [addForm] = Form.useForm<{ name: string; base_url: string }>();

  // ---- "配置 SSH" Modal 状态 (gpu-backend-install-on-wake) ----
  const [sshOpen, setSshOpen] = useState(false);
  const [sshTarget, setSshTarget] = useState<Backend | null>(null);
  const [sshSubmitting, setSshSubmitting] = useState(false);
  const [sshForm] = Form.useForm<UpdateBackendSSHInput>();

  // ---- 自动扩缩 (autoscale) 卡片状态 ----
  const [autoscaleForm] = Form.useForm<AutoscaleConfig>();
  const [autoscaleLoading, setAutoscaleLoading] = useState(false);
  const [autoscaleSaving, setAutoscaleSaving] = useState(false);

  const loadAutoscale = useCallback(async () => {
    setAutoscaleLoading(true);
    try {
      const cfg = await getAutoscaleConfig();
      autoscaleForm.setFieldsValue({
        enabled: cfg.enabled,
        scale_up_pending: cfg.scale_up_pending,
        idle_shutdown_after_sec: cfg.idle_shutdown_after_sec,
        min_online: cfg.min_online,
      });
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载自动扩缩配置失败');
    } finally {
      setAutoscaleLoading(false);
    }
  }, [autoscaleForm]);

  useEffect(() => {
    void loadAutoscale();
  }, [loadAutoscale]);

  const handleSaveAutoscale = async () => {
    const v = await autoscaleForm.validateFields();
    setAutoscaleSaving(true);
    try {
      await putAutoscaleConfig({
        enabled: v.enabled,
        scale_up_pending: v.scale_up_pending,
        idle_shutdown_after_sec: v.idle_shutdown_after_sec,
        min_online: v.min_online,
      });
      message.success('自动扩缩配置已保存');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setAutoscaleSaving(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listBackends();
      setBackends(res.backends || []);
      setSummary(res.summary || EMPTY_SUMMARY);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async () => {
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      await createBackend({ name: v.name, base_url: v.base_url, api_key: v.api_key });
      message.success('backend 已注册');
      setOpen(false);
      form.resetFields();
      await refresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (extId: string) => {
    try {
      await deleteBackend(extId);
      message.success('backend 已删除');
      await refresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  const handleBoot = async (extId: string) => {
    setBusyId(extId);
    try {
      await bootBackend(extId);
      message.success('已发起开机, 实例启动 + ComfyUI 就绪需稍候, 健康检查随后更新状态');
      await refresh();
    } catch (e) {
      if (isNotConfigured(e)) {
        setOnethingDisabled(true);
        message.error('OneThingAI API 未配置');
      } else {
        message.error(e instanceof Error ? e.message : '开机失败');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleShutdown = async (extId: string) => {
    setBusyId(extId);
    try {
      await shutdownBackend(extId);
      message.success('已发起关机 (停止计费), 健康检查随后将标离线, 请稍候');
      await refresh();
    } catch (e) {
      if (isNotConfigured(e)) {
        setOnethingDisabled(true);
        message.error('OneThingAI API 未配置');
      } else {
        message.error(e instanceof Error ? e.message : '关机失败');
      }
    } finally {
      setBusyId(null);
    }
  };

  // 打开 "配置 SSH" — 预填已有 host/port/user (密码不下发, 留空=不改但需重填).
  const openSshConfig = (row: Backend) => {
    setSshTarget(row);
    sshForm.setFieldsValue({
      ssh_host: row.ssh_host ?? 'proxy-ai.onethingai.com',
      ssh_port: undefined as unknown as number,
      ssh_user: 'root',
      ssh_password: '',
    });
    setSshOpen(true);
  };

  const handleSaveSSH = async () => {
    if (!sshTarget) return;
    const v = await sshForm.validateFields();
    setSshSubmitting(true);
    try {
      await updateBackendSSH(sshTarget.ext_id, {
        ssh_host: v.ssh_host,
        ssh_port: v.ssh_port,
        ssh_user: v.ssh_user || 'root',
        ssh_password: v.ssh_password,
      });
      message.success('SSH 已保存 (密码加密入库, 首连自动钉扎 host key)');
      setSshOpen(false);
      setSshTarget(null);
      sshForm.resetFields();
      await refresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSshSubmitting(false);
    }
  };

  // 打开 "从 OneThingAI 添加实例", 拉全部实例.
  const openAddFromOnething = async () => {
    setAddOpen(true);
    setPicked(null);
    addForm.resetFields();
    setInstancesLoading(true);
    try {
      const res = await listOnethingInstances();
      // 后端未配 token → configured:false (200, 不抛错). 据此置灰.
      if (!res.configured) {
        setOnethingDisabled(true);
        setAddOpen(false);
        message.error('OneThingAI API 未配置');
        return;
      }
      setOnethingDisabled(false);
      setInstances(res.instances || []);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载 OneThingAI 实例失败');
    } finally {
      setInstancesLoading(false);
    }
  };

  // 已注册为 backend 的 appId 集合 → 标记/禁止重复添加.
  const registeredAppIds = new Set(
    backends.map((b) => b.onething_app_id).filter(Boolean) as string[],
  );

  const pickInstance = (inst: OnethingInstance) => {
    setPicked(inst);
    // base_url 默认从实例 webUI 推导, 允许用户改.
    addForm.setFieldsValue({
      name: inst.app_id,
      base_url: inst.web_ui || '',
    });
  };

  const handleRegisterFromOnething = async () => {
    if (!picked) {
      message.warning('请先选择一个实例');
      return;
    }
    const v = await addForm.validateFields();
    setRegistering(true);
    try {
      await createBackend({
        name: v.name,
        base_url: v.base_url,
        api_key: '',
        onething_app_id: picked.app_id,
      });
      message.success('实例已注册为 backend (已关联 OneThingAI)');
      setAddOpen(false);
      setPicked(null);
      addForm.resetFields();
      await refresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '注册失败');
    } finally {
      setRegistering(false);
    }
  };

  return (
    <>
      <Title level={4} style={{ margin: 0 }}>
        GPU 算力池
      </Title>
      <Paragraph type="secondary">
        平台级跨 App 共享的 Onething backend 池。健康检查探活 + 负载感知调度 + 故障转移：一台关机自动绕开
        （解决 502 单点），全部离线降级即梦 / flux 兜底链。
      </Paragraph>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="服务器总数" value={summary.total} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="在线数" value={summary.online} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="离线数" value={summary.offline} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="全局待执行任务数" value={summary.pending_total} />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 12 }} wrap>
        {(['online', 'idle', 'offline', 'maintenance'] as BackendStatus[]).map((s) => (
          <Tag key={s} color={STATUS_META[s].color}>
            {STATUS_META[s].label} {summary[s]}
          </Tag>
        ))}
      </Space>

      {onethingDisabled && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="OneThingAI API 未配置"
          description="实例级开/关机与 “从 OneThingAI 添加实例” 已停用。配置 OneThingAI token 后可用 (手填 URL 的 backend 不受影响)。"
        />
      )}

      <Card
        size="small"
        title="自动扩缩"
        loading={autoscaleLoading}
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          按负载自动开关 GPU 实例：排队多了自动开机，闲置久了自动关机省钱。关闭后完全人工控制。
        </Paragraph>
        <Form form={autoscaleForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="enabled"
                label="自动扩缩"
                valuePropName="checked"
                tooltip="总开关；关闭后不自动开/关机，完全人工"
              >
                <Switch checkedChildren="开" unCheckedChildren="关" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="scale_up_pending"
                label="开机阈值"
                rules={[{ required: true, type: 'number', min: 1, message: '至少 1' }]}
                extra="人均排队超过 N 个任务才自动开机"
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="idle_shutdown_after_sec"
                label="闲置关机 (秒)"
                rules={[{ required: true, type: 'number', min: 60, message: '至少 60 秒' }]}
                extra="实例闲置满 N 秒自动关机省钱 (≥60)"
              >
                <InputNumber min={60} step={60} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="min_online"
                label="常驻最少台数"
                rules={[{ required: true, type: 'number', min: 0, message: '不能小于 0' }]}
                extra="常驻最少 N 台 (>0 会持续计费)"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" loading={autoscaleSaving} onClick={handleSaveAutoscale}>
            保存
          </Button>
        </Form>
      </Card>

      <Card
        size="small"
        title="backends"
        extra={
          <Space>
            <Tooltip title={onethingDisabled ? 'OneThingAI API 未配置' : undefined}>
              <Button
                icon={<CloudServerOutlined />}
                onClick={openAddFromOnething}
                disabled={onethingDisabled}
              >
                从 OneThingAI 添加实例
              </Button>
            </Tooltip>
            <Button icon={<PlusOutlined />} type="primary" onClick={() => setOpen(true)}>
              新增 backend
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              刷新
            </Button>
          </Space>
        }
      >
        <Table<Backend>
          rowKey="ext_id"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={backends}
          columns={[
            { title: 'ID', dataIndex: 'ext_id', render: (v: string) => <code>{v}</code> },
            { title: '名称', dataIndex: 'name' },
            {
              title: '状态',
              dataIndex: 'status',
              render: (s: BackendStatus) => {
                const meta = STATUS_META[s] ?? { color: 'default', label: s };
                return <Tag color={meta.color}>{meta.label}</Tag>;
              },
            },
            {
              title: 'GPU',
              dataIndex: 'gpu_available',
              render: (v: boolean | null | undefined) => <GpuAvailabilityTag available={v} />,
            },
            { title: '任务数', dataIndex: 'running_count', align: 'right' },
            { title: '待执行任务数', dataIndex: 'pending_count', align: 'right' },
            {
              title: 'OneThingAI',
              key: 'onething',
              render: (_, row) =>
                row.onething_app_id ? (
                  <Tooltip title={`appId: ${row.onething_app_id}`}>
                    <Tag color="geekblue">已关联</Tag>
                  </Tooltip>
                ) : (
                  <Tag color="default">手填 URL</Tag>
                ),
            },
            {
              title: '引擎就绪',
              key: 'engine_readiness',
              render: (_, row) => (
                <EngineReadinessMatrix readiness={row.engine_readiness} />
              ),
            },
            {
              title: '更新时间',
              dataIndex: 'last_heartbeat_at',
              render: (v: string | null) => (v ? formatDateTime(v) : '—'),
            },
            {
              title: '操作',
              key: 'actions',
              render: (_, row) => {
                const linked = Boolean(row.onething_app_id);
                const canControl = linked && !onethingDisabled;
                const disabledTip = onethingDisabled
                  ? 'OneThingAI API 未配置'
                  : !linked
                    ? '未关联 OneThingAI 实例'
                    : undefined;
                const rowBusy = busyId === row.ext_id;
                return (
                  <Space size="small">
                    <Tooltip title={disabledTip}>
                      <Button
                        type="link"
                        size="small"
                        icon={<ThunderboltOutlined />}
                        disabled={!canControl || rowBusy}
                        loading={rowBusy}
                        onClick={() => handleBoot(row.ext_id)}
                      >
                        开机
                      </Button>
                    </Tooltip>
                    <Tooltip title={disabledTip}>
                      <Popconfirm
                        title="关闭该实例?"
                        description={
                          row.running_count > 0
                            ? `⚠ 当前有 ${row.running_count} 个任务在执行 (可能正在出图), 关机会中断它们。确认关机?`
                            : '实例关机后停止计费, 健康检查随后标离线。重新开机需稍候 ComfyUI 就绪。'
                        }
                        okText="关机"
                        okButtonProps={{ danger: true }}
                        disabled={!canControl || rowBusy}
                        onConfirm={() => handleShutdown(row.ext_id)}
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<PoweroffOutlined />}
                          disabled={!canControl || rowBusy}
                          loading={rowBusy}
                        >
                          关机
                        </Button>
                      </Popconfirm>
                    </Tooltip>
                    <Tooltip
                      title={
                        row.ssh_host
                          ? `已配 SSH: ${row.ssh_host} — install-on-wake 可自动装节点链`
                          : '未配 SSH — install-on-wake 需 SSH 才能自动装节点链'
                      }
                    >
                      <Button
                        type="link"
                        size="small"
                        onClick={() => openSshConfig(row)}
                      >
                        {row.ssh_host ? 'SSH ✓' : '配置 SSH'}
                      </Button>
                    </Tooltip>
                    <Popconfirm
                      title="删除该 backend?"
                      description="软删, 调度池立即排除它, 历史不丢."
                      onConfirm={() => handleDelete(row.ext_id)}
                      okText="删除"
                      okButtonProps={{ danger: true }}
                    >
                      <Button type="link" danger size="small">
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                );
              },
            },
          ]}
        />
      </Card>

      <Modal
        title="新增 backend"
        open={open}
        onOk={handleCreate}
        confirmLoading={submitting}
        onCancel={() => setOpen(false)}
        okText="创建"
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="如 onething-gpu-01" />
          </Form.Item>
          <Form.Item name="base_url" label="base_url" rules={[{ required: true }]}>
            <Input placeholder="https://xxx.onethingai.com" />
          </Form.Item>
          <Form.Item
            name="api_key"
            label="api_key"
            rules={[{ required: true }]}
            tooltip="加密入库 (auth_blob_enc), 明文绝不落库"
          >
            <Input.Password placeholder="backend 凭证" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="从 OneThingAI 添加实例"
        open={addOpen}
        onOk={handleRegisterFromOnething}
        confirmLoading={registering}
        onCancel={() => setAddOpen(false)}
        okText="注册为 backend"
        okButtonProps={{ disabled: !picked }}
        destroyOnClose
        width={680}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          列出 OneThingAI 账号下全部实例。选一个未注册的 → 注册为 backend 并自动关联其
          appId，base_url 默认从实例 webUI 推导 (可改)。
        </Paragraph>
        <Table<OnethingInstance>
          rowKey="app_id"
          size="small"
          loading={instancesLoading}
          pagination={false}
          dataSource={instances}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: picked ? [picked.app_id] : [],
            onChange: (_keys, rows) => rows[0] && pickInstance(rows[0]),
            getCheckboxProps: (inst) => ({
              disabled: registeredAppIds.has(inst.app_id),
            }),
          }}
          columns={[
            { title: 'appId', dataIndex: 'app_id', render: (v: string) => <code>{v}</code> },
            {
              title: '状态',
              dataIndex: 'status',
              render: (s: OnethingInstanceStatus) => {
                const meta = instanceStatusMeta(s);
                return <Tag color={meta.color}>{meta.label}</Tag>;
              },
            },
            { title: 'GPU', dataIndex: 'gpu_type' },
            {
              title: '已注册',
              key: 'registered',
              render: (_, inst) =>
                registeredAppIds.has(inst.app_id) ? (
                  <Tag color="green">已注册</Tag>
                ) : (
                  <Tag color="default">未注册</Tag>
                ),
            },
          ]}
        />
        {picked && (
          <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="name" label="名称" rules={[{ required: true }]}>
              <Input placeholder="如 onething-gpu-01" />
            </Form.Item>
            <Form.Item
              name="base_url"
              label="base_url"
              rules={[{ required: true }]}
              tooltip="ComfyUI 访问地址, 默认从实例 webUI 推导, 必要时手改"
            >
              <Input placeholder="https://xxx.onethingai.com" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={`配置 SSH — ${sshTarget?.name ?? ''}`}
        open={sshOpen}
        onOk={handleSaveSSH}
        confirmLoading={sshSubmitting}
        onCancel={() => {
          setSshOpen(false);
          setSshTarget(null);
        }}
        okText="保存"
        destroyOnClose
        width={520}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          OneThingAI API 不暴露 SSH，需从控制台手动填。保存后 install-on-wake 可在该
          backend 缺节点时自动 SSH 跑 restore 脚本。密码 AES-256-GCM 加密入库，首连自动
          钉扎 host key（改 SSH 信息会重新钉扎）。
        </Paragraph>
        <Form form={sshForm} layout="vertical">
          <Form.Item
            name="ssh_host"
            label="SSH Host"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input placeholder="proxy-ai.onethingai.com" />
          </Form.Item>
          <Form.Item
            name="ssh_port"
            label="SSH 端口"
            rules={[{ required: true, type: 'number', min: 1, message: '必填' }]}
            tooltip="OneThingAI 控制台该实例的 SSH 端口 (per-instance)"
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="如 39526" />
          </Form.Item>
          <Form.Item name="ssh_user" label="用户" initialValue="root">
            <Input placeholder="root" />
          </Form.Item>
          <Form.Item
            name="ssh_password"
            label="密码"
            rules={[{ required: true, message: '必填' }]}
            tooltip="加密入库, 明文绝不落库 / 不下发"
          >
            <Input.Password placeholder="该实例 SSH 密码" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
